import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const EditSubjectLevelClass = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    ClassName: '',
    Form: '',
    ClassType: '',
    Subject: '',
    gradelevelclass: '',
    Teacher: '',
    TeacherName: '',
    Term: '',
    Year: '',
  });
  const [teachers, setTeachers] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClassDetails();
    fetchTeachers();
    fetchClassTypes();
  }, [classId]);

  const fetchClassDetails = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/subjectlevelclass/${classId}`);
      const classData = response.data;
      setFormData({
        ClassName: classData.ClassName || '',
        Form: classData.Form || '',
        ClassType: classData.ClassType || '',
        Subject: classData.Subject || '',
        gradelevelclass: classData.gradelevelclass || '',
        Teacher: classData.Teacher || '',
        TeacherName: classData.TeacherName || '',
        Term: classData.Term || '',
        Year: classData.Year || '',
      });
    } catch (error) {
      console.error('Error fetching class details:', error);
      toast.error('Failed to fetch class details.');
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/createemployee/teachers/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Failed to fetch teachers.');
    }
  };

  const fetchClassTypes = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/class-types`);
      setClassTypes(response.data);
    } catch (error) {
      console.error('Error fetching class types:', error);
      toast.error('Failed to fetch class types.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTeacherChange = (e) => {
    const selectedTeacher = teachers.find((teacher) => teacher.EmployeeNumber === e.target.value);
    setFormData({
      ...formData,
      Teacher: selectedTeacher ? selectedTeacher.EmployeeNumber : '',
      TeacherName: selectedTeacher ? `${selectedTeacher.name} ${selectedTeacher.surname}` : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !formData.ClassName ||
      !formData.Form ||
      !formData.ClassType ||
      !formData.Subject ||
      !formData.Teacher ||
      !formData.Term ||
      !formData.Year
    ) {
      toast.error('Please fill in all fields.');
      setLoading(false);
      return;
    }

    try {
      await axios.put(`${BASE_URL}/subjectlevelclass/${classId}`, formData);
      toast.success('Class updated successfully!');
      navigate(`/view-class/${classId}`);
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Edit Subject Level Class</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Name</label>
                  <input
                    type="text"
                    name="ClassName"
                    value={formData.ClassName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                  <select
                    name="Form"
                    value={formData.Form}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Form</option>
                    {[1, 2, 3, 4, 5, 6].map((form) => (
                      <option key={form} value={form}>Form {form}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                  <select
                    name="ClassType"
                    value={formData.ClassType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Class Type</option>
                    {classTypes.map((type) => (
                      <option key={type.ClassTypeId} value={type.ClassTypeName}>
                        {type.ClassTypeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    name="Subject"
                    value={formData.Subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                  <select
                    name="Teacher"
                    value={formData.Teacher}
                    onChange={handleTeacherChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Teacher</option>
                    {Array.isArray(teachers) && teachers.map((teacher) => (
                      <option key={teacher.EmployeeNumber} value={teacher.EmployeeNumber}>
                        {`${teacher.name} ${teacher.surname}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                  <select
                    name="Term"
                    value={formData.Term}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Term</option>
                    {[1, 2, 3].map((term) => (
                      <option key={term} value={term}>Term {term}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    name="Year"
                    value={formData.Year}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Year</option>
                    {Array.from({ length: 78 }, (_, i) => 2023 + i).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(`/view-class/${classId}`)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-white bg-pink-600 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Class'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EditSubjectLevelClass;
